// Shared
import { getTextWidth } from '../shared/helper';

// Components
import Actions from '../components/listing/Actions/Actions';
import Filter from '../components/listing/Filter/Filter';
import FormInput from '../components/detail/FormInput/FormInput';

// Taxonomies
import { countryCore, countryFilter } from '../taxonomies/country';

// Gearbox core
export function gearboxCore() {
  const endpointSimple = '/gearboxes/simple';
  const endpoint = '/gearboxes';
  const endpointDescriptionVersion = '/descriptionversion/type/gearbox/id';
  const detailId = 'gearboxId';
  const detailHash = 'gearboxHash';

  const getDetailId = (data = null) => {
    if (!data) {
      return;
    }

    if (Array.isArray(data)) {
      return data[0][detailId];
    }

    return data[detailId];
  };

  const getDetailHash = (data = null) => {
    if (!data) {
      return;
    }

    if (Array.isArray(data)) {
      return data[0][detailHash];
    }

    return data[detailHash];
  };

  return {
    endpoint,
    endpointSimple,
    endpointDescriptionVersion,
    detailId,
    detailHash,
    getDetailId: getDetailId,
    getDetailHash: getDetailHash
  };
}

// Gearbox filter
export function gearboxFilter() {
  const parameter = 'GearboxId';

  // Get rows
  const getRows = (data) => {
    const rows = [];

    data.forEach((row, i) => {
      // Text/numeric values
      rows[i] = {
        value: row.gearboxId,
        code: row.gearboxCode
      };

      // Object values
      const objects = {
        label: row.gearboxDescription
      };

      // Get the text/value from the default culture
      Object.entries(objects).forEach(([j, prop]) => {
        prop.translations.forEach((culture, k) => {
          if (culture.defaultCulture === true) {
            rows[i][j] = culture.text;
          }
        });
      });
    });

    return rows;
  };

  return {
    parameter,
    getRows: getRows
  };
}

// Gearbox listing
export function gearboxListing() {
  // Initial sort
  const initialSort = { field: 'id', sort: 'asc' };

  // Initial filter values
  const initialFilters = {};

  // Get columns
  // columnsCustomized is used in case there is a hidden column
  const getColumns = (rows, columnsCustomized) => {
    const columns =
      columnsCustomized.length > 0
        ? columnsCustomized
        : [
            {
              field: 'actions',
              headerName: 'Actions',
              sortable: false,
              renderCell: function (params) {
                return (
                  <Actions
                    typeOfData="taxonomy"
                    id={params.row.id}
                    hash={params.row.hash}
                    endpoint="/transmission"
                    deleteApiEndpoint="/gearboxes"
                  />
                );
              },
              hideFromColumnsSelector: true
            },
            // DataGrid requires the "id" column to be unique,
            // so this is a unique/fake ID required by DataGrid
            {
              field: 'id',
              headerName: 'Unique ID',
              hide: true,
              hideFromColumnsSelector: true
            },
            // DataGrid requires the "id" column to be unique,
            // so this is the real ID to show on the columns for the user
            {
              field: 'realId',
              headerName: 'ID',
              hide: true
            },
            {
              field: 'hash',
              headerName: 'Hash',
              hide: true,
              hideFromColumnsSelector: true
            },
            { field: 'enabled', headerName: 'Enabled' },
            { field: 'country', headerName: 'Country' },
            { field: 'code', headerName: 'Code' },
            { field: 'description', headerName: 'Description' }
          ];

    const columnsResized = columns;

    columns.forEach((column, i) => {
      const id = column.field;
      let text = column.headerName;

      const textWidth = getTextWidth(text);
      columnsResized[i].width = textWidth;

      rows.forEach((row, j) => {
        if (row[id]) {
          const text = row[id];
          const textWidth = getTextWidth(text);

          if (columnsResized[i].width < textWidth) {
            columnsResized[i].width = textWidth;
          }
        }
      });
    });

    return columnsResized;
  };

  // Get rows
  const getRows = (data) => {
    const rows = [];

    data.forEach((row, i) => {
      // Text/numeric values
      rows[i] = {
        id: i,
        realId: row.gearboxId,
        hash: row.gearboxHash,
        enabled: row.gearboxEnabled ? 'Yes' : 'No',
        country: row.countryName,
        code: row.gearboxCode
      };

      // Object values
      const objects = {
        description: row.gearboxDescription
      };

      // Get the text/value from the default culture
      Object.entries(objects).forEach(([j, prop]) => {
        prop.translations.forEach((culture, k) => {
          if (culture.defaultCulture === true) {
            rows[i][j] = culture.text;
          }
        });
      });
    });

    return rows;
  };

  // Get filters
  const getFilters = () => {
    // Taxonomies
    const countryCore_ = countryCore();
    const countryFilter_ = countryFilter();

    return [
      [
        {
          size: { xs: 12 },
          component: (
            <Filter
              type="select"
              parameter={countryFilter_.parameter.toLowerCase()}
              label="Country"
              endpoint={countryCore_.endpoint}
              getRows={countryFilter_.getRows}
            />
          )
        }
      ]
    ];
  };

  return {
    initialSort,
    initialFilters,
    getColumns: getColumns,
    getRows: getRows,
    getFilters: getFilters
  };
}

// Gearbox detail
export function gearboxDetail() {
  const baseUrl = '/transmission';

  const getInputs = (data = null, prePopulate = false) => {
    // set an object of all the field values
    // where the default state is empty for each
    let detailObj = {
      gearboxId: '',
      gearboxHash: '',
      gearboxCode: '',
      gearboxDescription: '',
      gearboxDescriptionText: '',
      gearboxEnabled: false,
      countryId: ''
    };

    if (data !== null) {
      // Use the data provided to populate the default value of the fields
      Object.entries(detailObj)
        .filter(([i, prop]) => {
          // we cannot rely on a truthy check here,
          // b/c the minor version values can be a zero
          return data[i] != null
        })
        .forEach(([i, prop]) => {
          detailObj[i] = data[i];
        });

      // Get the text/value from the default culture
      if (
        detailObj.gearboxDescription &&
        detailObj.gearboxDescription.translations
      ) {
        detailObj.gearboxDescription.translations.forEach((culture, k) => {
          if (culture.defaultCulture === true) {
            detailObj.gearboxDescriptionText = culture.text;
          }
        });
      }
    }

    // Taxonomies
    const countryCore_ = countryCore();
    const countryFilter_ = countryFilter();

    const gearboxCore_ = gearboxCore();

    return [
      // Data Version (description.MajorVersion + description.MinorVersion)
      [
        {
          colSize: { xs: 12, md: 8, lg: 6 },
          size: { xs: 12 },
          className: "version-item-wrapper",
          component: (
            <FormInput
              type="version-control"
              inputProps={{
                itemType: "taxonomy",
                versionType: "description",
                versionLabel: "Version",
                endPoint: gearboxCore_.endpointDescriptionVersion,
                itemId: detailObj.gearboxId,
                majorVersion: detailObj.gearboxDescription.majorVersion,
                minorVersion: detailObj.gearboxDescription.minorVersion,
                dialogueTitle: "Data Version",
                ctaLabel: "Edit version"
              }}
            />
          )
        }
      ],
      // Country
      [
        {
          colSize: { xs: 12, md: 8, lg: 6 },
          size: { xs: 12 },
          component: (
            <FormInput
              type="select"
              parameter={countryCore_.detailId}
              label="Country"
              required={true}
              disabled={prePopulate ? true : false}
              endpoint={countryCore_.endpoint}
              getRows={countryFilter_.getRows}
              inputValue={detailObj.countryId}
              isCulturesSource={prePopulate ? false : true}
              dispatchEventOnChange={prePopulate ? false : true}
            />
          )
        }
      ],
      // Gearbox Code
      [
        {
          colSize: { xs: 12, md: 8, lg: 6 },
          size: { xs: 12 },
          component: (
            <FormInput
              type="text"
              parameter="gearboxCode"
              label="Code"
              required={true}
              inputValue={detailObj.gearboxCode}
            />
          )
        }
      ],
      // Description
      [
        {
          colSize: { xs: 12, md: 8, lg: 6 },
          size: { xs: 12 },
          component: (
            <FormInput
              type="localization"
              parameter="gearboxDescription"
              label="Description"
              required={true}
              inputValue={{
                key: detailObj.gearboxDescription.key
                  ? detailObj.gearboxDescription.key
                  : '',
                translations: detailObj.gearboxDescription.translations
                  ? detailObj.gearboxDescription.translations
                  : [],
                defaultCultureText: detailObj.gearboxDescriptionText
              }}
              dependOn={[{ filter: 'countryid', control: 'countryId' }]}
            />
          )
        }
      ],
      // Enabled
      [
        {
          colSize: { xs: 12, md: 8, lg: 6 },
          size: { xs: 12 },
          component: (
            <FormInput
              type="checkbox"
              label="Enabled"
              parameter="gearboxEnabled"
              required={true}
              inputValue={detailObj.gearboxEnabled}
            />
          )
        }
      ]
    ];
  };

  return {
    baseUrl,
    getInputs: getInputs
  };
}
